import { App } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../app";
import { getLocalizedCardCopy } from "../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../utils/get-plural-of-project-name";

/**
 * Replicates the preamble every card in the 3,600-line `buildOverview()`
 * monolith (#2222) used to share: `itemName`, `itemNamePlural`,
 * `projectName`, `projectNamePlural` and a `getCardCopy` closure derived from
 * them, all computed once from `app.getAppState()`.
 *
 * Each `addXCards` function below calls this itself rather than receiving
 * the values through a shared context object. Since the orchestrator
 * (`OverviewPlugin.buildOverview`) still calls each of them exactly once per
 * build, in the same synchronous pass, this reproduces the original
 * "captured once per build" semantics exactly — including the handful of
 * cards whose static `layoutProps` or `mapState` callback reads the raw
 * `language`/`settings` captured here instead of the per-render values their
 * own callback parameters would give them (e.g. the duration card in
 * `cards/team-and-duration.ts`, or the `yLabel` in
 * `cards/completed-projects-line-charts.ts`). That staleness is a
 * pre-existing characteristic of the monolith, not something this split
 * introduces or fixes.
 */
export function getOverviewCardCopy(app: App<TestAppState>) {
  const state = app.getAppState();
  const { language, settings } = state;
  const resolvedLanguage = language || "en";
  const itemName = getItemName(
    settings?.configuration?.itemName,
    resolvedLanguage
  );
  const itemNamePlural = getPluralOfItemName(
    settings?.configuration?.itemName,
    resolvedLanguage
  );
  const projectName = getProjectName(
    settings?.configuration?.projectName,
    resolvedLanguage
  );
  const projectNamePlural = getPluralOfProjectName(
    settings?.configuration?.projectName,
    resolvedLanguage
  );
  const categoryLabel =
    settings?.configuration?.fieldsDefinition?.find(
      (field) => field.fieldName === "category"
    )?.label ?? getLocalizedMessage(resolvedLanguage, "category");
  const getCardCopy = (
    currentLanguage: string,
    key: Parameters<typeof getLocalizedCardCopy>[1],
    params: Parameters<typeof getLocalizedCardCopy>[2] = {}
  ) =>
    getLocalizedCardCopy(currentLanguage, key, {
      itemName,
      itemNamePlural,
      projectName,
      projectNamePlural,
      categoryLabel,
      ...params,
    });

  return {
    language,
    settings,
    itemName,
    itemNamePlural,
    projectName,
    projectNamePlural,
    getCardCopy,
  };
}
